import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import type { Translations } from "@/lib/i18n/types";
import type { FieldKey } from "./fields";

/**
 * Edit state for the metadata row: which field's popover is open (only ever
 * one) and the in-flight save. A failed save keeps the popover open so the
 * typed value isn't lost, and surfaces the error as a toast.
 */
export function useFieldEditor(
  onFieldsSave: (fields: Record<string, string>) => Promise<void>,
  t: Translations
) {
  const [openField, setOpenField] = useState<FieldKey | null>(null);
  const [isSavingField, setIsSavingField] = useState(false);
  const { toast } = useToast();

  const saveField = async (key: FieldKey, value: string) => {
    setIsSavingField(true);
    try {
      await onFieldsSave({ [key]: value });
      setOpenField(null);
    } catch {
      toast({
        title: t.common.error,
        description: t.buildingDetail.dashboard.fieldsSaveError,
        variant: "destructive",
      });
    } finally {
      setIsSavingField(false);
    }
  };

  return { openField, setOpenField, isSavingField, saveField };
}
